import { useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import { KeyRoundIcon } from 'lucide-react'
import { useState } from 'react'
import { currentUserQuery } from '@/lib/auth'
import { contactLabel } from '@/lib/contact-label'
import { type GroupUser, useGroupSupportSessions, useGroupUsers } from './api'
import { GroupUserTwoFactorReset } from './group-user-two-factor-reset'

export function GroupUsers({ groupId }: { groupId: string }) {
  const { t } = useTranslation()
  const messages = useAppErrorMessages()
  const users = useGroupUsers(groupId, true)
  const me = useQuery(currentUserQuery)
  const sessions = useGroupSupportSessions(groupId)
  const [resetting, setResetting] = useState<GroupUser | null>(null)
  // The API takes the reset only inside the admin's OWN live session here.
  const inSession = (sessions.data ?? []).some(
    (session) => session.is_active && session.platform_admin_id === me.data?.id,
  )

  if (users.isLoading) {
    return (
      <div data-testid="group-users-loading">
        <ListSkeleton columns={[2, 2, 1, 1, 1, 1]} rows={5} />
      </div>
    )
  }

  if (users.isError) {
    return (
      <div data-testid="group-users-error">
        <AppError error={users.error} messages={messages} retry={users.refetch} />
      </div>
    )
  }

  const list = users.data ?? []
  if (list.length === 0) {
    return (
      <div data-testid="group-users-empty">
        <EmptyState
          title={t('groups.users.emptyTitle')}
          description={t('groups.users.emptyBody')}
          className="border border-border py-8"
        />
      </div>
    )
  }

  return (
    <section data-testid="group-users-panel" aria-labelledby="group-users-heading">
      <h2 id="group-users-heading" className="sr-only">
        {t('groups.users.title')}
      </h2>
      {!inSession && list.some((user) => user.two_factor_enabled) ? (
        <p className="text-small text-muted-foreground mb-3" data-testid="group-users-2fa-hint">
          {t('groups.users.twoFactor.hint')}
        </p>
      ) : null}
      <Table data-testid="group-users-table" className="border border-border">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead>{t('groups.users.columns.name')}</TableHead>
            <TableHead>{t('groups.users.columns.email')}</TableHead>
            <TableHead className="text-end">{t('groups.users.columns.companies')}</TableHead>
            <TableHead>{t('groups.users.columns.owner')}</TableHead>
            <TableHead>{t('groups.users.columns.twoFactor')}</TableHead>
            <TableHead className="text-end">{t('groups.users.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((user) => (
            <TableRow key={user.id} data-testid={`group-user-${user.id}`}>
              <TableCell className="text-body">{user.name}</TableCell>
              <TableCell className="text-small font-mono">
                <bdi dir="ltr">{contactLabel(user.email, user.login_phone)}</bdi>
              </TableCell>
              <TableCell className="text-end font-mono tabular-nums">
                {user.company_count}
              </TableCell>
              <TableCell>
                {user.is_company_owner_somewhere ? (
                  <Badge tone="success">{t('groups.users.owner')}</Badge>
                ) : null}
              </TableCell>
              <TableCell>
                {user.two_factor_enabled ? (
                  <Badge tone="neutral">{t('groups.users.twoFactor.on')}</Badge>
                ) : null}
              </TableCell>
              <TableCell className="text-end">
                {user.two_factor_enabled ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!inSession}
                    onClick={() => setResetting(user)}
                    data-testid={`group-user-2fa-reset-${user.id}`}
                    aria-label={t('groups.users.twoFactor.resetFor', { name: user.name })}
                    title={t('groups.users.twoFactor.reset')}
                  >
                    <KeyRoundIcon
                      className={`size-3.5 ${inSession ? 'text-danger' : 'text-muted-foreground'}`}
                      aria-hidden="true"
                    />
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {resetting ? (
        <GroupUserTwoFactorReset
          groupId={groupId}
          user={resetting}
          onClose={() => setResetting(null)}
        />
      ) : null}
    </section>
  )
}
